/**
 * RetailerAnalytics (/retaileradmin/analytics)
 *
 * The Trusscore analytics charts over the retailer's own estimates
 * (/api/retailer-admin-analytics.php, cached per retailer + store scope).
 * Every category label is translated from its KEY — never from the English
 * label the API carries — so the page reads correctly in fr-CA / es-MX.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, RefreshCw, Loader2, Clock, CalendarDays, DollarSign } from 'lucide-react';
import { PieChart, type PieSlice } from '../admin/charts/PieChart';
import { BarChart, type BarDatum } from '../admin/charts/BarChart';
import '../admin/charts/charts.css';
import { useRetailerContext } from './RetailerAdminContext';
import { fetchRetailerAnalytics, type AnalyticsPayload, type AnalyticsCategoryRow } from '../../services/retailerAdminApi';
import { calculationModeLabel, spaceTypeLabel } from '../../components/results/configDataPoints';
import { prettify } from '../../utils/estimateLabels';

const PALETTE = ['#1a1a2e', '#f5b400', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const formatHour = (h: number, locale: string): string => {
  const d = new Date(2000, 0, 1, h, 0, 0);
  return d.toLocaleTimeString(locale, { hour: 'numeric' });
};

/** Weekday name for 0 (Sun) … 6 (Sat) in the viewer's language. */
const weekdayLabel = (dow: number, locale: string): string => {
  const d = new Date(2000, 0, 2 + dow); // 2000-01-02 was a Sunday
  return d.toLocaleDateString(locale, { weekday: 'short' });
};

export const RetailerAnalytics: React.FC = () => {
  const { t, i18n } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [animationNonce, setAnimationNonce] = useState(0);

  const load = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setIsRefreshing(true); else setIsLoading(true);
    setError(false);
    try {
      setData(await fetchRetailerAnalytics(forceRefresh));
      setAnimationNonce(n => n + 1);
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load, ctx.retailerId]);

  const locale = i18n.language;
  const presetLabel = (key: string, fallback: string): string => {
    if (key === 'custom') return t('wizard:shared.custom');
    const k = `wizard:walls.presets.${key}.name`;
    return i18n.exists(k) ? t(k) : fallback;
  };
  const slices = (rows: AnalyticsCategoryRow[], label: (r: AnalyticsCategoryRow) => string): PieSlice[] =>
    rows.map(r => ({ key: r.key, label: label(r), value: r.count }));

  if (isLoading && !data) {
    return (
      <div className="bg-white rounded-3 shadow-sm p-5 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 400 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} className="text-muted mb-3" />
        <div className="text-muted small">{t('analytics.loading')}</div>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="bg-white rounded-3 shadow-sm p-4">
        <div className="alert alert-warning mb-3">{t('analytics.loadFailed')}</div>
        <button className="btn btn-primary btn-sm" onClick={() => load(true)}>{t('analytics.retry')}</button>
      </div>
    );
  }
  if (!data) return null;

  const hourBars: BarDatum[] = data.hourOfDay.map(h => ({ key: String(h.hour), label: formatHour(h.hour, locale), value: h.avgPerDay }));
  const dowBars: BarDatum[] = data.dayOfWeek.map(d => ({ key: String(d.dow), label: weekdayLabel(d.dow, locale), value: d.avgPerWeek }));
  const calcModeSlices = slices(data.calculationMode, r => calculationModeLabel(r.key));
  const presetSlices = slices(data.designPreset, r => presetLabel(r.key, r.label));
  const spaceTypeSlices = slices(data.spaceType, r => spaceTypeLabel(r.key, prettify(r.key) ?? r.label));
  const costRangeSlices = slices(data.costRangeCAD ?? [], r => r.label);
  const sum = (s: PieSlice[]) => s.reduce((acc, d) => acc + d.value, 0);
  const fmt = (n: number) => n.toLocaleString(locale);
  const avgFmt = (n: number) => (n >= 1 ? n.toFixed(1) : n.toFixed(2));

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><BarChart3 size={22} /> {t('analytics.title')}</h2>
          <p className="text-muted mb-0 small">{t('analytics.subtitle', { retailer: ctx.retailerName })}</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small d-flex align-items-center gap-1">
            <Clock size={13} />
            {t('analytics.updated', { when: new Date(data.generatedAt).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }) })}
          </span>
          <button
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => load(true)}
            disabled={isRefreshing}
            title={t('analytics.refreshTitle')}
          >
            <RefreshCw size={13} style={isRefreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
            {t('analytics.refresh')}
          </button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1"><BarChart3 size={13} /> {t('analytics.totalEstimates')}</div>
            <div className="fs-2 fw-bold">{fmt(data.totals.estimates)}</div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1"><DollarSign size={13} /> {t('analytics.totalValue')}</div>
            <div className="fs-2 fw-bold">{(data.totals.totalValueCAD ?? 0).toLocaleString(locale, { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })}</div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1"><CalendarDays size={13} /> {t('analytics.window')}</div>
            <div className="fs-5 fw-semibold">{t('analytics.days', { count: Math.round(data.totals.elapsedDays) })}</div>
            <div className="text-muted small">{t('analytics.weeks', { count: Math.round(data.totals.weeksElapsed) })}</div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1"><Clock size={13} /> {t('analytics.firstToLast')}</div>
            <div className="small">
              {data.totals.firstAt ? new Date(data.totals.firstAt).toLocaleDateString(locale) : '—'}
              {' → '}
              {data.totals.lastAt ? new Date(data.totals.lastAt).toLocaleDateString(locale) : '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>{t('analytics.cost.title')}</h5>
            <div className="chart-subtitle">{t('analytics.cost.subtitle')}</div>
            <PieChart key={`cost-${animationNonce}`} data={costRangeSlices} palette={PALETTE} centerLabel={fmt(sum(costRangeSlices))} centerSubLabel={t('analytics.estimatesUnit')} />
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>{t('analytics.mode.title')}</h5>
            <div className="chart-subtitle">{t('analytics.mode.subtitle')}</div>
            <PieChart key={`calc-${animationNonce}`} data={calcModeSlices} palette={PALETTE} centerLabel={fmt(sum(calcModeSlices))} centerSubLabel={t('analytics.estimatesUnit')} />
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>{t('analytics.preset.title')}</h5>
            <div className="chart-subtitle">{t('analytics.preset.subtitle')}</div>
            <PieChart key={`preset-${animationNonce}`} data={presetSlices} palette={PALETTE} centerLabel={fmt(sum(presetSlices))} centerSubLabel={t('analytics.fullRoomUnit')} />
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>{t('analytics.space.title')}</h5>
            <div className="chart-subtitle">{t('analytics.space.subtitle')}</div>
            <PieChart key={`space-${animationNonce}`} data={spaceTypeSlices} palette={PALETTE} centerLabel={fmt(sum(spaceTypeSlices))} centerSubLabel={t('analytics.estimatesUnit')} />
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <div className="analytics-chart-card">
            <h5>{t('analytics.hour.title')}</h5>
            <div className="chart-subtitle">{t('analytics.hour.subtitle', { days: fmt(Math.round(data.totals.elapsedDays)), timezone: data.timezone })}</div>
            <BarChart key={`hour-${animationNonce}`} data={hourBars} color={PALETTE[2]} peakColor={PALETTE[0]} formatValue={avgFmt} />
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="analytics-chart-card">
            <h5>{t('analytics.dow.title')}</h5>
            <div className="chart-subtitle">{t('analytics.dow.subtitle', { weeks: fmt(Math.round(data.totals.weeksElapsed)) })}</div>
            <BarChart key={`dow-${animationNonce}`} data={dowBars} color={PALETTE[3]} peakColor={PALETTE[0]} formatValue={avgFmt} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerAnalytics;
